import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  TextField,
  IconButton,
  Button,
  InputAdornment
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';

export default function SpeedMeetingChatDialog({
  open,
  onClose,
  meeting,
  userId,
  users,
  participants,
  onMeetingUpdated
}) {
  const [comments, setComments] = useState([]);
  const [newMessage, setNewMessage] = useState('');

  useEffect(() => {
    if (!open || !meeting) return;

    let parsedComments = [];

    if (meeting.user_comments) {
      try {
        // Primeiro tenta fazer parse diretamente
        parsedComments = JSON.parse(meeting.user_comments);
      } catch (firstError) {
        try {
          // Caso venha com caracteres escapados
          const cleanedComments = meeting.user_comments
            .replace(/\\"/g, '"')
            .replace(/^"|"$/g, '');

          parsedComments = JSON.parse(cleanedComments);
        } catch (secondError) {
          console.error('Error parsing comments:', secondError);

          // Fallback caso seja texto simples
          parsedComments = [{
            text: meeting.user_comments,
            timestamp: new Date().toISOString(),
            sender: 'system'
          }];
        }
      }

      if (!Array.isArray(parsedComments)) {
        parsedComments = [parsedComments];
      }
    }

    setComments(parsedComments);
    setNewMessage('');
  }, [open, meeting]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !meeting?.id || !userId) return;

    const message = {
      sender: userId,
      text: newMessage,
      timestamp: new Date().toISOString()
    };

    try {
      // Obter novamente as mensagens atuais
      const currentResponse = await fetch(
        `https://api.hub4food.colab4food.com/api/speedmeetings/${meeting.id}`
      );

      if (!currentResponse.ok) {
        throw new Error('Failed to fetch current speed meeting');
      }

      const currentData = await currentResponse.json();

      let currentComments = [];

      if (currentData.user_comments) {
        try {
          currentComments = JSON.parse(
            currentData.user_comments.replace(/\\"/g, '"')
          );

          if (!Array.isArray(currentComments)) {
            currentComments = [currentComments];
          }
        } catch {
          currentComments = [];
        }
      }

      const updatedComments = [...currentComments, message];

      const response = await fetch(
        `https://api.hub4food.colab4food.com/api/speedmeetings/${meeting.id}/comments`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            user_comments: updatedComments
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      const updatedMeeting = await response.json();

      setComments(updatedComments);
      setNewMessage('');

      // Atualiza também a tabela da página principal
      if (onMeetingUpdated) {
        onMeetingUpdated(updatedMeeting);
      }

    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const handleClose = () => {
    setNewMessage('');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle>
        Meeting Chat - {meeting?.subject}

        <Typography
          variant="subtitle2"
          color="text.secondary"
        >
          Participants: {
            participants?.length > 0
              ? participants.join(', ')
              : 'No participants'
          }
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box
          sx={{
            height: '400px',
            overflowY: 'auto',
            mb: 2,
            p: 2,
            backgroundColor: '#f5f5f5',
            borderRadius: 1
          }}
        >
          {Array.isArray(comments) && comments.length > 0 ? (
            comments.map((msg, index) => (
              <Box
                key={index}
                sx={{
                  mb: 2,
                  display: 'flex',
                  flexDirection:
                    msg.sender === userId
                      ? 'row-reverse'
                      : 'row'
                }}
              >
                <Box
                  sx={{
                    maxWidth: '70%',
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor:
                      msg.sender === userId
                        ? '#e3f2fd'
                        : '#ffffff',
                    boxShadow: 1,
                    wordBreak: 'break-word'
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                  >
                    {users.find(u => u.id === msg.sender)?.name || 'Unknown'} -
                    {' '}
                    {msg.timestamp
                      ? new Date(msg.timestamp).toLocaleString()
                      : ''}
                  </Typography>

                  <Typography variant="body1">
                    {msg.text}
                  </Typography>
                </Box>
              </Box>
            ))
          ) : (
            <Typography>
              No messages yet
            </Typography>
          )}
        </Box>

        <TextField
          fullWidth
          label="Type your message"
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          margin="normal"
          multiline
          rows={2}
          onKeyPress={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSendMessage();
            }
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={handleSendMessage}
                  disabled={!newMessage.trim()}
                  color="primary"
                >
                  <SendIcon />
                </IconButton>
              </InputAdornment>
            )
          }}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}